/* =========================================================================
   CcPaymentWithdraw.tsx – CCPayment crypto withdrawals

   Players request a withdrawal (the balance is already deducted at request
   time). A SuperAdmin approves it here with their 2FA code, which fires the
   CCPayment payout automatically, or rejects it, which refunds the player.
   CCPayment's webhook then flips the row to Success (with a txid) or Failed.
   ========================================================================= */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import { Check, ChevronLeft, ChevronRight, RefreshCw, ShieldCheck, X } from 'lucide-react';
import { format } from 'date-fns';
import { apiFetch } from '../../utils/api';
import AdminTwoFactorDialog from './AdminTwoFactorDialog';
import CcWithdrawActionDialog from './CcWithdrawActionDialog';
import { CcWithdrawRow, STATUS_COLOR, cardSx, cellSx, headSx, inputSx } from './types';

const STATUSES = ['pending', 'Processing', 'Success', 'Failed', 'Rejected', ''];
const PENDING = ['In Queue', 'Pending'];
const SIZE = 25;

const fmt = (iso: string | null) => (iso ? format(new Date(iso), 'yyyy-MM-dd HH:mm') : '-');

export default function CcPaymentWithdraw() {
  const [rows, setRows] = useState<CcWithdrawRow[]>([]);
  const [total, setTotal] = useState(0);
  const [load, setLoad] = useState(false);
  const [err, setErr] = useState('');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('pending');

  const [twoFaOpen, setTwoFaOpen] = useState(false);
  const [twoFaEnabled, setTwoFaEnabled] = useState<boolean | null>(null);
  const [action, setAction] = useState<{ kind: 'approve' | 'reject'; row: CcWithdrawRow } | null>(null);

  const fetchRows = useCallback(async () => {
    setLoad(true);
    setErr('');
    try {
      const qs = new URLSearchParams({ page: String(page), limit: String(SIZE) });
      if (status) qs.set('status', status);
      const res = await apiFetch<{ data: CcWithdrawRow[]; total: number }>(
        `/api/ccpayment/withdraw/list?${qs.toString()}`
      );
      setRows(res.data || []);
      setTotal(res.total || 0);
    } catch (e: any) {
      setErr(e?.message || 'Failed to load withdrawals');
      setRows([]);
    } finally {
      setLoad(false);
    }
  }, [page, status]);

  const fetch2Fa = useCallback(async () => {
    try {
      const res = await apiFetch<{ isEnabled: boolean }>('/api/ccpayment/2fa/status');
      setTwoFaEnabled(!!res.isEnabled);
    } catch {
      setTwoFaEnabled(null);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetch2Fa(); }, [fetch2Fa]);

  const pages = Math.max(1, Math.ceil(total / SIZE));

  return (
    <Box sx={{ p: 3 }}>
      {/* Header + filters */}
      <Paper sx={{ ...cardSx, p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography sx={{ color: '#F9F9F9', fontSize: '1rem', fontWeight: 700 }}>
              CCPayment Withdrawals
            </Typography>
            <Typography sx={{ color: '#8384A5', fontSize: '0.75rem' }}>
              Approve with your 2FA code to send automatically, or reject to refund the player
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              onClick={() => setTwoFaOpen(true)}
              startIcon={<ShieldCheck size={15} />}
              sx={{
                bgcolor: '#10182E', border: '1px solid #1E2D55', fontSize: '0.72rem',
                color: twoFaEnabled ? '#0ECC68' : '#FFC23F',
                '&:hover': { borderColor: '#2B6EF5' },
              }}
            >
              {twoFaEnabled === null ? '2FA' : twoFaEnabled ? '2FA on' : 'Set up 2FA'}
            </Button>

            <TextField
              select size="small" label="Status" value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              sx={{ ...inputSx, minWidth: 150 }}
            >
              {STATUSES.map((s) => (
                <MenuItem key={s || 'all'} value={s}>{s === 'pending' ? 'Pending' : s || 'All'}</MenuItem>
              ))}
            </TextField>

            <Tooltip title="Refresh">
              <Button onClick={fetchRows} sx={{ minWidth: 40, bgcolor: '#10182E', border: '1px solid #1E2D55', color: '#8384A5', '&:hover': { color: '#2B6EF5' } }}>
                <RefreshCw size={16} />
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {twoFaEnabled === false && (
        <Alert severity="warning" sx={{ mb: 2, bgcolor: 'rgba(255,194,63,0.1)', color: '#FFC23F', fontSize: '0.78rem' }}>
          You have not set up 2FA yet — approving and rejecting withdrawals needs it. Click “Set up 2FA”.
        </Alert>
      )}

      {err && (
        <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(224,27,79,0.1)', color: '#E01B4F' }}>{err}</Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={cardSx}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>ID</TableCell>
              <TableCell sx={headSx}>User</TableCell>
              <TableCell sx={headSx}>Branch / Handled by</TableCell>
              <TableCell sx={headSx}>Coin / Chain</TableCell>
              <TableCell sx={headSx}>Address</TableCell>
              <TableCell sx={headSx} align="right">Amount</TableCell>
              <TableCell sx={headSx}>Status</TableCell>
              <TableCell sx={headSx}>Tx</TableCell>
              <TableCell sx={headSx}>Requested</TableCell>
              <TableCell sx={headSx} align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {load ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ ...cellSx, textAlign: 'center', py: 4 }}>
                  <CircularProgress size={22} sx={{ color: '#2B6EF5' }} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ ...cellSx, textAlign: 'center', py: 4, color: '#8384A5' }}>
                  No withdrawals found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} hover sx={{ '&:hover': { bgcolor: '#162140' } }}>
                  <TableCell sx={cellSx}>#{r.id}</TableCell>
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#F9F9F9' }}>{r.username || `#${r.uid}`}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#8384A5' }}>{r.uid}</Typography>
                  </TableCell>
                  {/* A branch player's request also reaches that branch's agents,
                      routed by its INR value — so show who it belongs to and who acted. */}
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: '0.78rem', color: r.branch_name ? '#F9F9F9' : '#8384A5' }}>
                      {r.branch_name || 'Direct'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#8384A5' }}>{r.action_by || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#F9F9F9' }}>{r.coin?.toUpperCase()}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#8384A5' }}>{r.chain || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 170 }}>
                    <Tooltip title={r.wallet}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#8384A5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.wallet}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={cellSx} align="right">{Number(r.amount)} {r.coin?.toUpperCase()}</TableCell>
                  <TableCell sx={cellSx}>
                    <Tooltip title={r.reason || ''}>
                      <Chip
                        label={r.status}
                        size="small"
                        sx={{
                          bgcolor: `${STATUS_COLOR[r.status] || '#8384A5'}22`,
                          color: STATUS_COLOR[r.status] || '#8384A5',
                          fontSize: '0.68rem',
                          maxWidth: 120,
                        }}
                      />
                    </Tooltip>
                    {r.refunded && (
                      <Typography sx={{ fontSize: '0.62rem', color: '#0ECC68', mt: 0.3 }}>refunded</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 120 }}>
                    <Tooltip title={r.txid || ''}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#8384A5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.txid || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, color: '#8384A5' }}>{fmt(r.date)}</TableCell>
                  <TableCell sx={cellSx} align="center">
                    {PENDING.includes(r.status) ? (
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={twoFaEnabled ? 'Approve & send' : 'Set up 2FA first'}>
                          <span>
                            <Button
                              size="small"
                              disabled={!twoFaEnabled}
                              onClick={() => setAction({ kind: 'approve', row: r })}
                              sx={{ minWidth: 34, bgcolor: 'rgba(14,204,104,0.12)', color: '#0ECC68', '&:hover': { bgcolor: 'rgba(14,204,104,0.25)' }, '&.Mui-disabled': { color: '#3A3F5C', bgcolor: '#10182E' } }}
                            >
                              <Check size={15} />
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={twoFaEnabled ? 'Reject & refund' : 'Set up 2FA first'}>
                          <span>
                            <Button
                              size="small"
                              disabled={!twoFaEnabled}
                              onClick={() => setAction({ kind: 'reject', row: r })}
                              sx={{ minWidth: 34, bgcolor: 'rgba(224,27,79,0.12)', color: '#E01B4F', '&:hover': { bgcolor: 'rgba(224,27,79,0.25)' }, '&.Mui-disabled': { color: '#3A3F5C', bgcolor: '#10182E' } }}
                            >
                              <X size={15} />
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Typography sx={{ fontSize: '0.7rem', color: '#8384A5' }}>—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Typography sx={{ color: '#8384A5', fontSize: '0.75rem' }}>
          {total} total · page {page} / {pages}
        </Typography>
        <Button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} sx={{ minWidth: 36, color: '#8384A5', '&.Mui-disabled': { color: '#3A3F5C' } }}>
          <ChevronLeft size={16} />
        </Button>
        <Button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} sx={{ minWidth: 36, color: '#8384A5', '&.Mui-disabled': { color: '#3A3F5C' } }}>
          <ChevronRight size={16} />
        </Button>
      </Stack>

      <CcWithdrawActionDialog action={action} onClose={() => setAction(null)} onDone={fetchRows} />
      <AdminTwoFactorDialog open={twoFaOpen} onClose={() => setTwoFaOpen(false)} onChanged={fetch2Fa} />
    </Box>
  );
}
